import { useState } from "react";
import { X } from "lucide-react";
import { ExtendedProject } from "@/types/project";

// ============================================================================
// Types
// ============================================================================

interface Pipeline {
  id: number;
  name: string;
}

interface ProjectPipelinePanelProps {
  isOpen: boolean;
  onClose: () => void;
  project: ExtendedProject | null;
  pipelines: Pipeline[];
  onPipelineChange: (pipelineId: number) => Promise<void>;
}

// ============================================================================
// Component
// ============================================================================

export function ProjectPipelinePanel({
  isOpen,
  onClose,
  project,
  pipelines,
  onPipelineChange,
}: ProjectPipelinePanelProps) {
  const [pipelineSearch, setPipelineSearch] = useState("");

  const handleClose = () => {
    setPipelineSearch("");
    onClose();
  };

  const handlePipelineSelect = async (pipelineId: number) => {
    await onPipelineChange(pipelineId);
    handleClose();
  };

  const filteredPipelines = pipelines.filter((pipeline) =>
    pipeline.name.toLowerCase().includes(pipelineSearch.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="bg-background rounded-lg shadow-xl max-w-[400px] p-6 relative mx-auto">
        <button
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
          onClick={handleClose}
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <h3 className="text-lg font-semibold mb-4">Change Project Pipeline</h3>
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search pipelines..."
            className="w-full border rounded px-3 py-2 mb-2"
            value={pipelineSearch}
            onChange={(e) => setPipelineSearch(e.target.value)}
            autoFocus
          />
          <div className="flex-1 overflow-y-auto max-h-[300px]">
            {filteredPipelines.map((pipeline) => (
              <div
                key={pipeline.id}
                className={`flex items-center p-2 rounded cursor-pointer hover:bg-muted ${
                  pipeline.id === project?.pipeline_id ? "bg-muted" : ""
                }`}
                onClick={() => handlePipelineSelect(pipeline.id)}
              >
                <span>{pipeline.name}</span>
                {pipeline.id === project?.pipeline_id && (
                  <span className="ml-2 text-xs text-primary font-medium">
                    Current
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
        <button
          className="mt-2 w-full bg-muted text-primary border border-border py-2 rounded hover:bg-muted transition font-medium"
          onClick={handleClose}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

ProjectPipelinePanel.displayName = "ProjectPipelinePanel";
